import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../services/firebase";

import useFetchDomains from "../../hooks/useFetchDomains";
import useFetchUserProfile from "../../hooks/useFetchUserProfile";
import useSaveUserProfile from "../../hooks/useSaveUserProfile";

import "./ProfileScreen.css";

export default function ProfileScreen() {
    const navigate = useNavigate();

    const { domains } = useFetchDomains();
    const { profile, loadingProfile } = useFetchUserProfile();
    const { saveUserProfile } = useSaveUserProfile();

    const [domain, setDomain] = useState("");
    const [filteredDomains, setFilteredDomains] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);

    const [experience, setExperience] = useState("");
    const [currentCtc, setCurrentCtc] = useState("");
    const [expectedCtc, setExpectedCtc] = useState("");

    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setFilteredDomains(domains);
    }, [domains]);

    useEffect(() => {
        if (!searchTerm.trim()) setFilteredDomains(domains);
        else {
            setFilteredDomains(
                domains.filter((d) =>
                    d.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
    }, [searchTerm, domains]);

    useEffect(() => {
        if (!profile) return;

        if (profile.domainId) {
            setDomain(profile.domainId);
            const selected = domains.find((d) => d.id === profile.domainId);
            setSearchTerm(selected ? selected.name : "");
        }

        if (profile.experience !== undefined) setExperience(profile.experience);
        if (profile.currentCtc) setCurrentCtc(profile.currentCtc);
        if (profile.expectedCtc) setExpectedCtc(profile.expectedCtc);
    }, [profile, domains]);

    const handleNext = async () => {
        if (!domain || !experience || !currentCtc || !expectedCtc) {
            alert("Please fill all fields");
            return;
        }

        const user = auth.currentUser;
        if (!user) {
            alert("Please sign in again.");
            return;
        }

        setSaving(true);
        await saveUserProfile(user.uid, {
            domainId: domain,
            experience,
            currentCtc,
            expectedCtc,
        });

        navigate("/user/packs");
    };

    if (loadingProfile) return <p className="loading-text">Loading...</p>;

    return (
        <div className="profile-page-full">
            <h1 className="page-title">Complete Your Profile</h1>
            <p className="page-subtitle">We will personalize your interview journey based on your experience.</p>

            <div className="profile-large-card">

                {/* DOMAIN */}
                <div className="form-row">
                    <label className="form-label">Domain</label>

                    <div className="dropdown-container">
                        <input
                            type="text"
                            placeholder="Search and select domain..."
                            className="form-input"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setShowDropdown(true);
                            }}
                            onClick={() => setShowDropdown(true)}
                        />

                        {showDropdown && (
                            <div className="dropdown-list">
                                {filteredDomains.length === 0 ? (
                                    <div className="dropdown-item">No results</div>
                                ) : (
                                    filteredDomains.map((d) => (
                                        <div
                                            key={d.id}
                                            className="dropdown-item"
                                            onClick={() => {
                                                setDomain(d.id);
                                                setSearchTerm(d.name);
                                                setShowDropdown(false);
                                            }}
                                        >
                                            {d.name}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* EXPERIENCE */}
                <div className="form-row">
                    <label className="form-label">Years of Experience</label>
                    <select
                        value={experience}
                        onChange={(e) => setExperience(e.target.value)}
                        className="form-input"
                    >
                        <option value="">Select experience</option>
                        {[...Array(16).keys()].map((i) => (
                            <option key={i} value={i}>
                                {i} {i === 1 ? "year" : "years"}
                            </option>
                        ))}
                    </select>
                </div>

                {/* CURRENT CTC */}
                <div className="form-row">
                    <label className="form-label">Current CTC (LPA)</label>
                    <input
                        type="number"
                        className="form-input"
                        placeholder="Enter current CTC"
                        value={currentCtc}
                        onChange={(e) => setCurrentCtc(e.target.value)}
                    />
                </div>

                {/* EXPECTED CTC */}
                <div className="form-row">
                    <label className="form-label">Expected CTC (LPA)</label>
                    <input
                        type="number"
                        className="form-input"
                        placeholder="Enter expected CTC"
                        value={expectedCtc}
                        onChange={(e) => setExpectedCtc(e.target.value)}
                    />
                </div>

                <button
                    className="save-button"
                    onClick={handleNext}
                    disabled={saving}
                >
                    {saving ? "Saving..." : "Save & Continue →"}
                </button>
            </div>
        </div>
    );
}
